<template>
  <config-base :config-plan-tile-status="configPlanTileStatus" class="custom-avatars-config" @view-details="showPlanDetailDialog">
    <template #config>
    <div>
      <div>
        <span class="custom-avatars-config__item-label">{{ $t('billing.config.select_number_users') }}</span>
      </div>
      <input-number-with-popover
        ref="userInput"
        size="lg"
        controls-position="both"
        class="gap8"
        v-model="licenseNumber"
        :min="minLicenses"
        :max="maxLicenses"
        :label="`${title} select the number of licenses`"
        :increaseTips="increaseTips"
        :decreaseTips="decreaseTips"
        :dataTracking="{ buyInteraction: 'number of licenses', planName: planTrackingName }"
        :data-testid="`checkout-input-license-${selectedPlan ? selectedPlan.plan_code : ''}`"
        @change="updatePlanLicense"
      />
      <zm-banner show-close v-if="showLicenseMaxAlert" type="info" class="gap16 custom-avatars-config__license-alert">
        <p v-html="$t('billing.custom_avatars.addon_user_limit_tips', [title, $t(currentBasePlan.planNameKey)])"></p>
      </zm-banner>
      <zm-banner show-close v-if="mx_downSellLicenseErrorMessage" type="info" class="gap16 custom-avatars-config__license-alert">
        {{ mx_downSellLicenseErrorMessage }}
      </zm-banner>
      <config-cycle
        v-model="billingCycle"
        :show-cycle-alert="!basePlanInSelected"
        :showMonthlyPriceFromCycle="true"
        :billing-cycle-list="billingCycleList"
        :unit="unit"
        @switchCycleAction="updateBillingCycle"
      />
      <div class="custom-avatars-config__excess-notice">
        {{ $t('billing.custom_avatars.excess_charge_notice', { price: $money(selectedPlan.excessPriceCents) }) }}
      </div>
      <preview-payment class="gap24" />
    </div>
    </template>
    <template #footer v-if="isShowRemoveBtn">
      <zm-button
        data-testid="checkout-button-93"
        type="link"
        ghost
        @click="removePlanByKey"
        :aria-label="`remove ${title} plan`"
      >
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script>
import { get, pick, chain, includes } from 'lodash';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import ConfigBase from '@/components/config-base.vue';
import ConfigCycle from '@/components/service/config-billing-cycle/index.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';

import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';
import { getPlanTotalLicense } from '@/common/service/validate';
import plansMixin from '../mx/plan';
import addonMixin from '../mx/addon';
import planDetailsDialogMixin from './components/plan-details/plan-details-dialog';
import commonMixin from './mixin';
import { flowTypes } from '@/common/service/page-config';
import { planUnitMap } from '@/common/product-unit';
import { dependBasePlanTypeList } from "./utils"
import autofill from "@/plans/mx/autofill";
import { planTypes } from '@/common/constants';
import autoAddMixin, { showSyncAddonMessage } from "@/common/mixin/autoAdd";
import { getOnlinePlanName } from '@/common/product';
import _ from 'lodash';

const ConfigBaseComp = {
  name: 'CustomAvatarBase',
  extends: ConfigBase,
  mixins: [commonMixin],
  key: planTypes[planTypes.CUSTOM_AVATARS],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: true,
      unit: planUnitMap.user,
    };
  },
};

export default {
  name: 'CustomAvatarConfig',
  mixins: [commonMixin, plansMixin, addonMixin, planDetailsDialogMixin, autofill, autoAddMixin],
  components: { ConfigBase: ConfigBaseComp, ConfigCycle, InputNumberWithPopover, PreviewPayment },
  data() {
    return {
      billingCycle: '',
      licenseNumber: 1,
      unit: planUnitMap.user,
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'getCurrentBasePlanInfo',
      'getUserCurrentSubscriptions',
      'basicConfig',
      'currentEditPlan',
      'getSubscriptions',
    ]),

    currentPlanBasicInfo() {
      return pick(this.planList[0], ['min', 'max', 'unit', 'category']);
    },

    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },

    currentBasePlan() {
      return get(this.getCurrentBasePlanInfo, ['plans', 0], {});
    },

    previousQuantity() {
      return this.currentEditPlan?.cartInfo?.selected_number ?? this.selectedPlan.min;
    },

    disableDownSell() {
      // Check account MRR > 500?
      return this.basicConfig.downSellData.disableDownSell;
    },

    minLicenses() {
      if (this.disableDownSell || !this.isStandardPrice) {
        return this.previousQuantity;
      }
      return this.selectedPlan.min;
    },

    getCurrentBasePlanInfoList() {
      let plans = this.getSelectedPlans ?? [];
      if (![flowTypes.buyNow, flowTypes.basic2pro].includes(window.$context.flow)) {
        plans = this.getUserCurrentSubscriptions.filter((item) => !item.isFreeTrial) ?? [];
      }

      const billingCycleDependBasePlanTypeList = this.selectedPlan?.billingCycleDependBasePlanTypeList ?? [];
      const basePlans = plans.filter((item) => billingCycleDependBasePlanTypeList.includes(item.type));
      return basePlans;
    },

    dependentBasePlanNameList() {
      return _.chain(this.getCurrentBasePlanInfoList)
        .map((item) => getOnlinePlanName(item))
        .filter(Boolean)
        .value();
    },

    basePlanTotalLicenses() {
      const plans = this.getCurrentBasePlanInfoList;
      return getPlanTotalLicense(plans);
    },

    maxLicenses() {
      if (this.mx_canAutoAdd) {
        return this.mx_maxAutoAddLicense;
      }
      return Math.min(
        Math.max(this.basePlanTotalLicenses, this.selectedPlan.cartInfo?.selected_number ?? 0),
        this.selectedPlan.max,
      );
    },

    /**
     * license number increase disabled tips
     * if base plan in selected plans show tips
     * else show 'Return to Plan Management' alert
     */
    increaseTips() {
      if (this.mx_canAutoAdd) {
        return '';
      }
      if (this.basePlanInSelected && this.licenseNumber >= this.basePlanTotalLicenses) {
        return this.$t('billing.licences_limit_tips', [this.title, _.join(this.dependentBasePlanNameList, ', ')]);
      }
      return '';
    },

    /**
     * license number decrease disabled tips
     */
    decreaseTips() {
      if (this.licenseNumber === this.previousQuantity) {
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }
        if (this.forNonStandardPriceTips) {
          return this.forNonStandardPriceTips;
        }
      }
      return '';
    },

    /**
     * Show 'Return to Plan Management' alert
     * if base plan in selected plans, no need to show
     */
    showLicenseMaxAlert() {
      if (this.mx_canAutoAdd) {
        return false;
      }
      if (!this.basePlanInSelected && this.licenseNumber >= this.basePlanTotalLicenses) {
        return true;
      }
      return false;
    },

    /**
     * implementing within plansMixin
     * disable billing cycle
     */
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = dependBasePlanTypeList;
      return chain(this.getUserCurrentSubscriptions)
        .filter((p) => includes(dependCyclePlanList, p.type))
        .filter(p => !p.cancelled)
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicense() {
      return (billingCycle) => {
        return (
          this.isEditFlow &&
          this.basicConfig?.isBulkLicenseAccount &&
          this.currentEditPlan?.billing_cycle !== billingCycle
        );
      };
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },

    /**
     * Is current base plan in selected plans
     */
    basePlanInSelected() {
      return this.getSelectedPlans.some((plan) => plan.type === this.currentBasePlan.type);
    },

    planTrackingName() {
      const key = this.selectedPlan?.planNameKey;
      return key && translateToEn(key) || "";
    },
  },
  watch: {
    maxLicenses(max) {
      if (this.licenseNumber > max) {
        this.licenseNumber = max;
        this.updatePlanLicense(max);
        showSyncAddonMessage(this.$t(this.selectedPlan.planNameKey), this.$t(this.currentBasePlan.planNameKey));
      }
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),

    setCartLicense(license) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: license },
      });
    },
    /**
     * update license number
     * @param {number} newLicense
     */
    updatePlanLicense(newLicense) {
      this.setCartLicense(newLicense);
      this.mx_validDownSellLicense(newLicense);
      if (this.mx_canAutoAdd) {
        this.mx_handlePlanLicenseChange(newLicense).catch((previousLicense) => {
          this.licenseNumber = previousLicense;
          this.setCartLicense(previousLicense);
        });
      }
    },

    /**
     * update billing cycle
     * @param {object} oldPlan
     * @param {object} newPlan
     */
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction('subscription cycle', `${this.planTrackingName} - ${newPlan.billing_cycle}`);

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.licenseNumber } },
      ]);
    },

    mx_onBasePlanLicenseChange(newLicense) {
      if (this.licenseNumber > newLicense) {
        this.licenseNumber = newLicense;
        this.setCartLicense(newLicense);
        this.mx_showSyncAddonMessage();
      }
    },
  },

  created() {
    if (this.selectedPlan?.billing_cycle) {
      this.billingCycle = this.selectedPlan.billing_cycle;
      this.licenseNumber = this.selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.licenseNumber);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

$sub-font-color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
$main-font-color: var(--zoom-color-text-stronger-neutral, #2A2B2D);

.main-color {
  color: $theme-color;
}

:deep(.gap8) {
  margin-top: 8px;
}
.gap16 {
  margin-top: 16px;
}
.gap24 {
  margin-top: 24px;
}

.custom-avatars-config {
  :deep .opc-zm-base-card {
    &__name {
      text-transform: none;
    }
    &__main--desc__content {
      font-size: 16px;
      color: $sub-font-color;
    }
  }

  &__item-label {
    font-size: 16px;
    font-weight: 590;
    color: $main-font-color;
    line-height: 20px;
  }

  &__tips-content {
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }

  &__licenses-info {
    color: $sub-font-color;
    font-size: 14px;
    line-height: 16.8px;
  }

  &__license-alert {
    padding: 16px;
    color: $sub-font-color;
    font-size: 14px;
    line-height: 21px;
    a {
      font-weight: 450;
    }
  }

  &__excess-notice {
    margin-top: 24px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
  }
}
</style>
